import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Users,
  FileImage,
  MessageSquare,
  Eye,
  LogOut,
  TrendingUp,
  Star,
  Calendar,
  BarChart3,
  Loader2,
  RefreshCw,
  EyeOff,
  Settings,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import AdminChart from "@/components/AdminChart";

interface AnalyticsData {
  total_users: number;
  total_designs: number;
  total_feedbacks: number;
  total_visits: number;
  total_downloads: number;
  new_users_week: number;
  new_designs_week: number;
  new_feedbacks_week: number;
  visits_week: number;
  downloads_week: number;
  avg_rating: number;
  most_active_day: string;
}

interface Feedback {
  id: string;
  user_email: string | null;
  rating: number;
  category: string;
  message: string | null;
  created_at: string;
  status: string;
}

interface PopularPage {
  page_path: string;
  visit_count: number;
  unique_visitors: number;
}

interface FeedbackStat {
  category: string;
  count: number;
  avg_rating: number;
}

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [recentFeedbacks, setRecentFeedbacks] = useState<Feedback[]>([]);
  const [popularPages, setPopularPages] = useState<PopularPage[]>([]);
  const [feedbackStats, setFeedbackStats] = useState<FeedbackStat[]>([]);

  // Chart data state
  const [dailyVisits, setDailyVisits] = useState<any[]>([]);
  const [userRegistrations, setUserRegistrations] = useState<any[]>([]);
  const [feedbackRatings, setFeedbackRatings] = useState<any[]>([]);
  const [feedbackCategories, setFeedbackCategories] = useState<any[]>([]);
  const [pagePopularity, setPagePopularity] = useState<any[]>([]);
  const [hourlyActivity, setHourlyActivity] = useState<any[]>([]);

  // Chart loading states
  const [chartsLoading, setChartsLoading] = useState<{
    [key: string]: boolean;
  }>({});

  // Hidden toggle state
  const [guidesHidden, setGuidesHidden] = useState<boolean>(false);
  const [toggleLoading, setToggleLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please login to access admin dashboard");
        navigate("/admin");
        return;
      }

      // Check if user has admin role using the simple admin function
      const { data: adminCheck, error: adminError } = await supabase
        .rpc("is_admin" as any)
        .single();

      if (adminError || !adminCheck) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/");
        return;
      }

      // User is admin, load dashboard data
      await loadDashboardData();
      await loadAdminSettings();
    } catch (error) {
      console.error("Error checking admin access:", error);
      toast.error("Failed to verify admin access");
      navigate("/admin");
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load essential data first (analytics summary)
      const { data: analyticsData, error: analyticsError } = await supabase
        .rpc("get_admin_analytics" as any)
        .single();

      if (analyticsError) throw analyticsError;
      setAnalytics(analyticsData as AnalyticsData);

      // Show the dashboard immediately with analytics data
      setLoading(false);

      // Load secondary data in parallel (non-blocking)
      const secondaryPromises = [
        // Load recent feedbacks
        supabase
          .from("user_feedbacks" as any)
          .select("*")
          .order("created_at", { ascending: false })
          .limit(10),
        // Load popular pages
        supabase.rpc("get_popular_pages" as any, { days_back: 7 }),
        // Load feedback statistics
        supabase.rpc("get_feedback_stats" as any),
      ];

      const [feedbacksResult, pagesResult, statsResult] =
        await Promise.allSettled(secondaryPromises);

      // Process secondary data results
      if (
        feedbacksResult.status === "fulfilled" &&
        feedbacksResult.value.data
      ) {
        setRecentFeedbacks(
          (feedbacksResult.value.data as unknown as Feedback[]) || []
        );
      }

      if (pagesResult.status === "fulfilled" && pagesResult.value.data) {
        setPopularPages((pagesResult.value.data as PopularPage[]) || []);
      }

      if (statsResult.status === "fulfilled" && statsResult.value.data) {
        setFeedbackStats((statsResult.value.data as FeedbackStat[]) || []);
      }

      // Load chart data in background (non-blocking)
      loadChartData();
    } catch (error: any) {
      console.error("Error loading dashboard data:", error);
      toast.error("Failed to load dashboard data");
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      // Load all charts in parallel for better performance
      const chartPromises = [
        // Daily visits
        supabase.rpc("get_daily_visits_chart" as any, { days_back: 30 }),
        // User registrations
        supabase.rpc("get_user_registration_chart" as any, { days_back: 30 }),
        // Feedback ratings
        supabase.rpc("get_feedback_ratings_chart" as any),
        // Feedback categories
        supabase.rpc("get_feedback_categories_chart" as any),
        // Page popularity
        supabase.rpc("get_page_popularity_chart" as any, { days_back: 7 }),
        // Hourly activity
        supabase.rpc("get_hourly_activity_chart" as any, { days_back: 7 }),
      ];

      const [
        dailyResult,
        userResult,
        ratingsResult,
        categoriesResult,
        pagesResult,
        hourlyResult,
      ] = await Promise.allSettled(chartPromises);

      // Process results
      if (dailyResult.status === "fulfilled" && dailyResult.value.data) {
        setDailyVisits(
          (dailyResult.value.data as any[]).map((item) => ({
            ...item,
            date: new Date(item.date).toLocaleDateString(),
          }))
        );
      }

      if (userResult.status === "fulfilled" && userResult.value.data) {
        setUserRegistrations(
          (userResult.value.data as any[]).map((item) => ({
            ...item,
            date: new Date(item.date).toLocaleDateString(),
          }))
        );
      }

      if (ratingsResult.status === "fulfilled" && ratingsResult.value.data) {
        setFeedbackRatings(
          (ratingsResult.value.data as any[]).map((item) => ({
            name: `${item.rating} Stars`,
            value: item.count,
          }))
        );
      }

      if (
        categoriesResult.status === "fulfilled" &&
        categoriesResult.value.data
      ) {
        setFeedbackCategories(
          (categoriesResult.value.data as any[]).map((item) => ({
            name: item.category,
            count: item.count,
            avg_rating: item.avg_rating,
          }))
        );
      }

      if (pagesResult.status === "fulfilled" && pagesResult.value.data) {
        setPagePopularity(
          (pagesResult.value.data as any[]).map((item) => ({
            name: item.page_path,
            visits: item.visits,
            unique_visitors: item.unique_visitors,
          }))
        );
      }

      if (hourlyResult.status === "fulfilled" && hourlyResult.value.data) {
        setHourlyActivity(
          (hourlyResult.value.data as any[]).map((item) => ({
            hour: `${item.hour}:00`,
            visits: item.visits,
          }))
        );
      }
    } catch (error) {
      console.error("Error loading chart data:", error);
    }
  };

  const loadAdminSettings = async () => {
    try {
      const { data, error } = await supabase.rpc("get_admin_setting" as any, {
        setting_key_param: "designer_guides_hidden",
      });

      if (error) throw error;

      if (data) {
        setGuidesHidden(data === true || data === "true");
      }
    } catch (error) {
      console.error("Error loading admin settings:", error);
    }
  };

  const toggleGuidesHidden = async () => {
    setToggleLoading(true);
    try {
      const newValue = !guidesHidden;

      const { error } = await supabase.rpc("set_admin_setting" as any, {
        setting_key_param: "designer_guides_hidden",
        setting_value_param: newValue,
        description_param:
          "Controls whether users can toggle positioning guides in the designer",
      });

      if (error) throw error;

      setGuidesHidden(newValue);
      toast.success(
        `Designer guides ${newValue ? "hidden" : "enabled"} successfully`
      );
    } catch (error: any) {
      console.error("Error toggling guides setting:", error);
      toast.error("Failed to update setting. Please try again.");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out successfully");
    navigate("/admin");
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      love: "bg-red-500",
      improve: "bg-blue-500",
      bug: "bg-orange-500",
      feature: "bg-purple-500",
    };
    return colors[category] || "bg-gray-500";
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      "default" | "secondary" | "destructive" | "outline"
    > = {
      new: "destructive",
      reviewed: "secondary",
      addressed: "default",
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-lg text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Kente Weave Design Analytics
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={guidesHidden ? "destructive" : "outline"}
                onClick={toggleGuidesHidden}
                disabled={toggleLoading}
                title={
                  guidesHidden ? "Show Designer Guides" : "Hide Designer Guides"
                }
              >
                {guidesHidden ? (
                  <EyeOff className="mr-2 h-4 w-4" />
                ) : (
                  <Settings className="mr-2 h-4 w-4" />
                )}
                {guidesHidden ? "Show Guides" : "Hide Guides"}
              </Button>
              <Button
                variant="outline"
                onClick={loadDashboardData}
                disabled={loading}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analytics?.total_users || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{analytics?.new_users_week || 0} this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saved Designs
              </CardTitle>
              <FileImage className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analytics?.total_designs || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{analytics?.new_designs_week || 0} this week
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Total designs saved by users
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Downloads</CardTitle>
              <Download className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analytics?.total_downloads || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{analytics?.downloads_week || 0} this week
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG & JPEG exports
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Site Visits</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analytics?.total_visits || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{analytics?.visits_week || 0} this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Feedbacks</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analytics?.total_feedbacks || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Avg rating: {analytics?.avg_rating || 0} ⭐
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analytics */}
        <Tabs defaultValue="charts" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 lg:w-[800px]">
            <TabsTrigger value="charts">Charts</TabsTrigger>
            <TabsTrigger value="downloads">Downloads</TabsTrigger>
            <TabsTrigger value="feedbacks">Feedbacks</TabsTrigger>
            <TabsTrigger value="pages">Popular Pages</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
          </TabsList>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Daily Visits Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Visits (30 Days)</CardTitle>
                  <CardDescription>
                    Site visits and unique visitors over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dailyVisits.length > 0 ? (
                    <AdminChart
                      type="line"
                      data={dailyVisits}
                      dataKey="visits"
                      nameKey="date"
                      height={300}
                      title="Daily Visits"
                    />
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p>Loading chart data...</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Registrations Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>User Registrations (30 Days)</CardTitle>
                  <CardDescription>
                    New user registrations over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {userRegistrations.length > 0 ? (
                    <AdminChart
                      type="bar"
                      data={userRegistrations}
                      dataKey="new_users"
                      nameKey="date"
                      height={300}
                      title="New Users"
                    />
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p>Loading chart data...</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Feedback Ratings Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Feedback Ratings Distribution</CardTitle>
                  <CardDescription>
                    Distribution of user ratings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {feedbackRatings.length > 0 ? (
                    <AdminChart
                      type="pie"
                      data={feedbackRatings}
                      dataKey="value"
                      height={300}
                      title="Rating Distribution"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No rating data available
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Feedback Categories Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Feedback Categories</CardTitle>
                  <CardDescription>Feedback by category</CardDescription>
                </CardHeader>
                <CardContent>
                  {feedbackCategories.length > 0 ? (
                    <AdminChart
                      type="bar"
                      data={feedbackCategories}
                      dataKey="count"
                      nameKey="name"
                      height={300}
                      title="Feedback Categories"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No category data available
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Page Popularity Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Page Popularity (7 Days)</CardTitle>
                  <CardDescription>
                    Most visited pages by total visits
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pagePopularity.length > 0 ? (
                    <AdminChart
                      type="bar"
                      data={pagePopularity}
                      dataKey="visits"
                      nameKey="name"
                      height={300}
                      title="Page Visits"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No page data available
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Hourly Activity Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Hourly Activity (7 Days)</CardTitle>
                  <CardDescription>
                    Site activity by hour of day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hourlyActivity.length > 0 ? (
                    <AdminChart
                      type="bar"
                      data={hourlyActivity}
                      dataKey="visits"
                      nameKey="hour"
                      height={300}
                      title="Hourly Visits"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No hourly data available
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Downloads Tab */}
          <TabsContent value="downloads" className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Download Statistics</CardTitle>
                  <CardDescription>
                    Overview of PNG and JPEG downloads
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics?.total_downloads || 0}
                      </div>
                      <div className="text-sm text-blue-600">
                        Total Downloads
                      </div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {analytics?.downloads_week || 0}
                      </div>
                      <div className="text-sm text-green-600">This Week</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    Tracks PNG and JPEG exports by authenticated users
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Download Activity</CardTitle>
                  <CardDescription>Recent download activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics?.total_downloads > 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      Download activity tracking is active
                      <br />
                      <span className="text-xs">
                        All PNG and JPEG exports are being recorded
                      </span>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <p>Loading download activity...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Feedbacks Tab */}
          <TabsContent value="feedbacks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Feedback</CardTitle>
                <CardDescription>
                  Latest feedback from users about their experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentFeedbacks.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center text-muted-foreground"
                          >
                            No feedback received yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentFeedbacks.map((feedback) => (
                          <TableRow key={feedback.id}>
                            <TableCell className="text-sm">
                              {new Date(
                                feedback.created_at
                              ).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-sm">
                              {feedback.user_email || "Anonymous"}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                <span className="font-medium">
                                  {feedback.rating}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={getCategoryColor(feedback.category)}
                              >
                                {feedback.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-sm">
                              {feedback.message || "-"}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(feedback.status)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Popular Pages Tab */}
          <TabsContent value="pages" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Most Visited Pages</CardTitle>
                <CardDescription>
                  Top 10 pages by visits in the last 7 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page Path</TableHead>
                      <TableHead>Total Visits</TableHead>
                      <TableHead>Unique Visitors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {popularPages.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center text-muted-foreground"
                        >
                          No visit data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      popularPages.map((page, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            {page.page_path}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Eye className="h-4 w-4 text-muted-foreground" />
                              {page.visit_count}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              {page.unique_visitors}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Feedback by Category</CardTitle>
                  <CardDescription>
                    Distribution of feedback types
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {feedbackStats.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No feedback statistics available
                      </p>
                    ) : (
                      feedbackStats.map((stat) => (
                        <div
                          key={stat.category}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <Badge className={getCategoryColor(stat.category)}>
                              {stat.category}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Avg: {stat.avg_rating}⭐
                            </span>
                          </div>
                          <div className="text-2xl font-bold">{stat.count}</div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Insights</CardTitle>
                  <CardDescription>Key metrics at a glance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">New Users This Week</p>
                      <p className="text-2xl font-bold">
                        {analytics?.new_users_week || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <BarChart3 className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium">
                        New Designs This Week
                      </p>
                      <p className="text-2xl font-bold">
                        {analytics?.new_designs_week || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="text-sm font-medium">Most Active Day</p>
                      <p className="text-lg font-bold">
                        {analytics?.most_active_day
                          ? new Date(
                              analytics.most_active_day
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Star className="h-5 w-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium">Average Rating</p>
                      <p className="text-2xl font-bold">
                        {analytics?.avg_rating || 0} / 5
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
